import { Component, Element, Event, EventEmitter, Method, Prop, State, Watch, h } from '@stencil/core';
import Bind from 'bind-decorator';

import { generateCssClasses } from '../../utils/generate-css-classes';
import { createMutationObserver } from '../../utils/mutation-observer';

@Component({
	shadow: true,
	styleUrl: 'zds-collapsible.scss',
	tag: 'zds-collapsible',
})
export class ZdsCollapsible {
	contentElement: HTMLDivElement;
	mutationObserver: MutationObserver;
	panelElement: HTMLDivElement;

	@Element() hostElement: HTMLDivElement;

	@Event() zdsCollapsibleMinHeightChanged: EventEmitter;

	@Event({ bubbles: true }) zdsCollapsibleClose: EventEmitter;
	@Event({ bubbles: true }) zdsCollapsibleClosed: EventEmitter;
	@Event({ bubbles: true }) zdsCollapsibleOpen: EventEmitter;
	@Event({ bubbles: true }) zdsCollapsibleOpened: EventEmitter;

	@Prop() minHeight: number = 0;
	@Prop() minLines: number = 0;
	@Prop({ reflect: true, mutable: true }) open: boolean = false;

	@State() calculatedMinHeight: number | null = null;

	@Method()
	async isCollapsingNeeded() {
		return this.panelElement.scrollHeight > this.calculatedMinHeight;
	}

	@Watch('calculatedMinHeight')
	onCalculatedMinHeightChange(newValue: number) {
		if (!this.open) {
			this.setHeight(newValue, false);
			this.onTransitionEnd();
		}

		requestAnimationFrame(() => {
			this.zdsCollapsibleMinHeightChanged.emit();
		});
	}

	@Watch('minHeight')
	async onMinHeightChange() {
		await this.recalculateMinHeight();
	}

	@Watch('minLines')
	async onMinLinesChange() {
		await this.recalculateMinHeight();
	}

	@Watch('open')
	onOpenChange(newValue: boolean, currentValue: boolean) {
		if (newValue !== currentValue) {
			this.toggle(this.open);
		}
	}

	@Bind
	handleMutation() {
		requestAnimationFrame(() => void this.recalculateMinHeight());
	}

	async componentDidLoad() {
		this.panelElement.addEventListener('transitionstart', () => this.onTransitionStart(), false);
		this.panelElement.addEventListener('transitionend', () => this.onTransitionEnd(true), false);

		await this.recalculateMinHeight();
		this.onTransitionEnd();

		this.mutationObserver = createMutationObserver(this.hostElement, this.handleMutation);
	}

	disconnectedCallback() {
		this.mutationObserver?.disconnect();
	}

	render() {
		const collapsibleClasses = generateCssClasses('zds-collapsible', {
			open: this.open,
		});

		return <div class={collapsibleClasses}>{this.renderPanel()}</div>;
	}

	renderPanel() {
		return (
			<div
				aria-hidden={this.shouldHaveAriaHidden()}
				class="zds-collapsible__panel no-transition"
				ref={(element) => (this.panelElement = element)}
			>
				<div
					class="zds-collapsible__content"
					ref={(element) => {
						this.contentElement = element;
					}}
				>
					<slot />
				</div>
			</div>
		);
	}

	shouldHaveAriaHidden() {
		if (this.minHeight || this.minLines) {
			return false;
		}

		return !this.open;
	}

	toggle(open: boolean) {
		if (this.open) {
			// Necessary to remove line clamp to get true scroll height of panel
			this.contentElement.style.webkitLineClamp = 'initial';
		}

		const targetPanelHeight = open ? this.panelElement.scrollHeight : this.calculatedMinHeight;

		this.open = open;
		this.setHeight(targetPanelHeight);
	}

	@Bind
	onTransitionStart() {
		this[`zdsCollapsible${this.open ? 'Open' : 'Close'}`].emit();

		if (this.open) {
			requestAnimationFrame(() => {
				this.panelElement.style.visibility = 'visible';
				this.contentElement.style.webkitLineClamp = 'initial'; // 'none' should work too, but doesn't
			});
		}
	}

	@Bind
	onTransitionEnd(emitEvent: boolean = false) {
		// If - after the animation finishes - the collapsible is actually open, the height should be set to
		//	auto and any overflow should be visible. This is necessary for a few edge-cases where the height
		// 	for animation could not be calculated properly.
		if (this.open) {
			requestAnimationFrame(() => {
				this.panelElement.style.height = 'auto';
				this.panelElement.style.overflow = 'visible';
			});
		} else {
			requestAnimationFrame(() => {
				// Hide the element to make sure its content is not focusable
				this.panelElement.style.visibility = this.minHeight > 0 || this.minLines > 0 ? 'visible' : 'hidden';
				this.contentElement.style.webkitLineClamp = this.minLines > 0 ? this.minLines.toString() : 'initial';
			});
		}

		if (emitEvent) {
			this[`zdsCollapsible${this.open ? 'Opened' : 'Closed'}`].emit();
		}
	}

	/**
	 * Makes sure that the height is adjusted properly for animation in conjunction with using height: auto.
	 *
	 * @param height the height that the panel should be set to
	 * @param animated whether or not to animate the height change, defaults to true
	 */
	setHeight(targetHeight: number, animated: boolean = true): void {
		if (this.minLines > 0) {
			this.panelElement.style.overflow = 'visible';
			this.panelElement.style.height = 'auto';
			return;
		}

		this.panelElement.style.overflow = 'hidden';

		this.panelElement.classList.toggle('no-transition', !animated);

		// When the current height is set to 'auto', the height needs to be adjusted accordingly, as animating
		//	from auto to 0 is not supported. Set the panelElement's height to its scrollHeight first.
		if (this.panelElement.style.height === 'auto' || this.panelElement.style.height === '') {
			requestAnimationFrame(() => {
				this.panelElement.style.height = `${this.panelElement.scrollHeight}px`;

				// Make sure that the height is actually set from auto to scrollHeight by requesting a new
				//	animationFrame.
				requestAnimationFrame(() => {
					this.panelElement.style.height = `${targetHeight}px`;

					requestAnimationFrame(() => {
						this.panelElement.classList.remove('no-transition');
					});
				});
			});
		} else {
			// Business as usual.
			requestAnimationFrame(() => {
				this.panelElement.style.height = `${targetHeight}px`;

				requestAnimationFrame(() => {
					this.panelElement.classList.remove('no-transition');
				});
			});
		}
	}

	/**
	 * minHeight is applied when the collapsible is closed. It determines whether closed state is actually completely
	 * 	closed, or whether we show a sneak peak of what's been encapsulated by collapsible.
	 *
	 * This function calculates the minHeight of the collapsible based on what's the smallest: the given minHeight or
	 * the actual content height.
	 */
	@Bind
	@Method()
	async recalculateMinHeight() {
		const lineHeightStr = window.getComputedStyle(this.contentElement).getPropertyValue('line-height');
		if (this.minLines && lineHeightStr === 'normal') {
			console.warn('zds-collapsible will have property `minLines` ignored because is has no line-height set');
		}
		const lineHeight = lineHeightStr === 'normal' ? 0 : parseFloat(lineHeightStr);
		const minHeightFromLines = this.minLines * lineHeight;

		const targetMinHeight = Math.max(this.minHeight, minHeightFromLines);

		this.calculatedMinHeight = Math.min(targetMinHeight, this.contentElement.scrollHeight);
	}
}
